import { useEffect } from "react";
import { HIREOS_INQUIRY_URL } from "@shared/hireos-links";
import { useSeo } from "@/lib/use-seo";
import {
  breadcrumbJsonLd,
  faqJsonLd,
  serviceJsonLd,
  servicesFaq,
} from "@/lib/structured-data";

const PAGE_TITLE = "Sound Hire Services | Accent Productions Wellington";

const PAGE_DESCRIPTION =
  "Professional sound equipment hire in Wellington for public events, corporate conferences, festivals, and private parties. Wireless mics, PA systems, line arrays, and full event audio with on-site technicians.";

/**
 * Ported from web/src/app/services/page.tsx (Next.js App Router).
 *
 * - next/metadata -> useSeo hook (#147): title, description, canonical,
 *   plus the Service / FAQ / Breadcrumb JSON-LD from services/layout.tsx.
 * - next/image -> plain <img> with absolute-fill classes.
 * - next/link -> react-router <Link>.
 * - The hash-anchor scroll effect is kept as-is (no Next APIs).
 */
const services = [
  {
    id: "public",
    title: "Public Events",
    description:
      "Wireless mics for speeches, speakers for music, and a tech to run it all. Outdoor setups with weatherproof gear included.",
    image: "/images/image1-public-waitangipark.webp",
    imageAlt:
      "Public event sound hire with PA system and wireless microphones at Waitangi Park Wellington",
    location: "Wellington CBD, New Zealand",
    includes: [
      "Handheld and lapel wireless mics",
      "Weatherproof outdoor PA systems",
      "Aux input for your own playlists",
      "Technician on-site for the duration",
    ],
  },
  {
    id: "corporate",
    title: "Corporate Events",
    description:
      "Mics, speakers, and a quiet setup for conferences, presentations, and company dos. We can feed to livestream or recording too.",
    image: "/images/image1-public-parilament.webp",
    imageAlt:
      "Corporate event audio hire with microphones and speakers at Parliament Wellington",
    location: "Parliament, Wellington",
    includes: [
      "Gooseneck, handheld, and lapel options",
      "Distributed speaker coverage for large rooms",
      "Feed to livestream or recording",
      "Quiet, unobtrusive setup",
    ],
  },
  {
    id: "festivals",
    title: "Festivals",
    description:
      "Multi-stage setups, all sizes of FOH systems for indoor or outdoor use including large format flown line arrays and/or ground-stacked systems and complete backlines.",
    image: "/images/image1-festival-slavfest.jpeg",
    imageAlt:
      "Festival sound system hire with line arrays and stage monitors at Slavfest Wellington",
    location: "Slavfest, Wellington",
    includes: [
      "Flown or ground-stacked line arrays",
      "Wedge and in-ear monitor options",
      "Full backline and DI setup",
      "Experienced crew for load-in and show",
    ],
  },
  {
    id: "parties",
    title: "Private Events",
    description:
      "Speakers, subs, and a mic for toasts. We drop it off, set it up, and pick it up the next day.",
    image: "/images/image2-private-newtown.jpeg",
    imageAlt:
      "Private event and party sound equipment hire with speakers and CDJs in Newtown Wellington",
    location: "Newtown, Wellington",
    includes: [
      "Compact PA with sub for up to 150 guests",
      "CDJs, mixer, or aux playback",
      "Wireless mic for toasts",
      "Same-day delivery and collection",
    ],
  },
];

export default function ServicesPage() {
  useSeo({
    title: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    canonical: "/services",
    jsonLd: [
      serviceJsonLd,
      faqJsonLd(servicesFaq),
      breadcrumbJsonLd([
        { name: "Home", path: "/" },
        { name: "Services", path: "/services" },
      ]),
    ],
  });

  useEffect(() => {
    // Hash anchor scroll. Land at the top of the section so the title is
    // visible (key context for the user). The sticky header is 5rem tall;
    // each section uses scroll-mt-24 to leave a small gap below it.
    const hash = window.location.hash;
    if (!hash) return;
    const raf = requestAnimationFrame(() => {
      const section = document.querySelector(hash) as HTMLElement | null;
      if (!section) return;
      // Instant jump — DESIGN.md Motion: no scroll effects.
      section.scrollIntoView({ block: "start" });
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <main className="min-h-screen">
      {/* Header — oversized page title */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-28 pb-10 md:pb-20">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[0.95] text-gray-900 mb-5 md:mb-6">
            Sound hire, sorted<span className="text-orange-600">.</span>
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-gray-600 max-w-2xl font-medium">
            We provide the gear and the crew. Tell us about your event and
            we&apos;ll sort out the rest.
          </p>
        </div>
      </section>

      {/* Services — numbered editorial sections */}
      <section className="bg-white pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16 md:space-y-28">
            {services.map((service, index) => (
              <div key={service.id} id={service.id} className="scroll-mt-24">
                <div className="w-full grid lg:grid-cols-2 gap-6 lg:gap-16 items-start">
                  {/* Image first on mobile so each title sits under its photo */}
                  <div
                    className={`order-2 ${index % 2 === 1 ? "lg:order-2" : "lg:order-1"}`}
                  >
                    <p className="text-sm font-medium text-orange-600 mb-3">
                      {String(index + 1).padStart(2, "0")}
                    </p>
                    <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-gray-900 mb-4 md:mb-6">
                      {service.title}
                    </h2>
                    <p className="text-gray-600 mb-8 text-lg md:text-xl font-medium">
                      {service.description}
                    </p>
                    <ul className="mb-8 md:mb-10 border-t border-gray-200">
                      {service.includes.map((item) => (
                        <li
                          key={item}
                          className="py-3 border-b border-gray-200 text-gray-700 font-medium"
                        >
                          {item}
                        </li>
                      ))}
                    </ul>
                    <a
                      href={HIREOS_INQUIRY_URL}
                      className="block sm:inline-block text-center bg-orange-600 text-white px-6 py-3 md:px-8 md:py-3.5 rounded-md font-bold text-base border border-orange-600"
                    >
                      Get a Quote
                    </a>
                  </div>
                  <div
                    className={`aspect-[4/3] relative rounded-md overflow-hidden order-1 ${
                      index % 2 === 1 ? "lg:order-1" : "lg:order-2"
                    }`}
                  >
                    <img
                      src={service.image}
                      alt={service.imageAlt || service.title}
                      loading={index === 0 ? "eager" : "lazy"}
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                    <p className="absolute bottom-3 right-3 text-white text-xs font-medium [text-shadow:0_1px_3px_rgba(0,0,0,0.85)]">
                      {service.location}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
